/*
 * Hydra Plus Pharma — single ES module, no dependencies.
 * Progressive enhancement only: every link, form and price works without this file.
 * Uses Shopify's AJAX Cart API + Section Rendering API so cart markup has one source (sections/cart-drawer).
 */
const config = JSON.parse(document.getElementById('ThemeConfig')?.textContent || '{}');
const routes = config.routes || {};
const text = config.text || {};
const live = document.getElementById('LiveRegion');
const announce = (msg) => { if (live) { live.textContent = ''; requestAnimationFrame(() => { live.textContent = msg; }); } };
const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

/* ---------- Dialog drawers (menu, cart, filters, search) ---------- */
let lastTrigger = null;
function openDialog(dialog, trigger) {
  if (!dialog || dialog.open) return;
  lastTrigger = trigger || document.activeElement;
  dialog.showModal();
  trigger?.setAttribute('aria-expanded', 'true');
}
function closeDialog(dialog) {
  if (!dialog?.open) return;
  dialog.close();
}
document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-drawer-open]');
  if (opener) { e.preventDefault(); openDialog(document.getElementById(opener.dataset.drawerOpen), opener); return; }
  const closer = e.target.closest('[data-drawer-close], [data-search-close]');
  if (closer) { closeDialog(closer.closest('dialog')); return; }
  // Click on the backdrop (outside the dialog box) closes it.
  if (e.target instanceof HTMLDialogElement && e.target.open) {
    const r = e.target.getBoundingClientRect();
    if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) closeDialog(e.target);
  }
});
document.addEventListener('close', (e) => {
  if (!(e.target instanceof HTMLDialogElement)) return;
  $$(`[aria-controls="${e.target.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
  lastTrigger?.focus?.();
}, true);

/* ---------- Search dialog + predictive search ---------- */
document.addEventListener('click', (e) => {
  const t = e.target.closest('[data-search-open]');
  if (!t) return;
  const dialog = document.getElementById('SearchDialog');
  if (!dialog) return;
  e.preventDefault();
  openDialog(dialog, t);
  $('input[type=search]', dialog)?.focus();
});

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = $('input[type=search]', this);
    this.results = $('[data-results]', this);
    this.timer = null;
    this.controller = null;
    this.input.addEventListener('input', () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.search(this.input.value.trim()), 220);
    });
    this.addEventListener('keydown', (e) => this.onKey(e));
  }
  async search(q) {
    if (q.length < 2) { this.results.innerHTML = ''; this.input.setAttribute('aria-expanded', 'false'); return; }
    this.controller?.abort();
    this.controller = new AbortController();
    const url = `${this.dataset.sectionUrl}?q=${encodeURIComponent(q)}&resources[type]=product,query,collection,article,page&resources[limit]=6&resources[options][unavailable_products]=last&section_id=predictive-search`;
    try {
      const res = await fetch(url, { signal: this.controller.signal });
      if (!res.ok) throw new Error(res.status);
      const doc = parseHTML(await res.text());
      const wrap = $('.predictive__wrap', doc);
      this.results.innerHTML = wrap ? wrap.outerHTML : '';
      this.input.setAttribute('aria-expanded', wrap ? 'true' : 'false');
    } catch (err) { if (err.name !== 'AbortError') this.results.innerHTML = ''; }
  }
  onKey(e) {
    const links = $$('a', this.results);
    if (!links.length || !['ArrowDown', 'ArrowUp'].includes(e.key)) return;
    e.preventDefault();
    const i = links.indexOf(document.activeElement);
    const next = e.key === 'ArrowDown' ? (i + 1) % links.length : (i <= 0 ? links.length - 1 : i - 1);
    links[next].focus();
  }
}
customElements.define('predictive-search', PredictiveSearch);

/* ---------- Cart ---------- */
const cartDrawer = () => document.getElementById('CartDrawer');
const cartSectionIds = () => {
  const ids = ['cart-drawer'];
  const page = $('[data-cart-page]')?.closest('[id^="shopify-section-"]');
  if (page) ids.push(page.id.replace('shopify-section-', ''));
  return ids;
};

function renderCartSections(sections) {
  if (!sections) return;
  Object.entries(sections).forEach(([id, html]) => {
    if (!html) return;
    const doc = parseHTML(html);
    if (id === 'cart-drawer') {
      const fresh = $('#CartDrawer', doc);
      const current = cartDrawer();
      if (fresh && current) {
        current.innerHTML = fresh.innerHTML;
        const count = fresh.dataset.cartCountValue;
        current.dataset.cartCountValue = count;
        $$('[data-cart-count]').forEach((el) => { el.textContent = count; el.hidden = count === '0'; });
      }
    } else {
      const target = document.getElementById(`shopify-section-${id}`);
      const fresh = doc.getElementById(`shopify-section-${id}`);
      if (target && fresh) target.innerHTML = fresh.innerHTML;
    }
  });
}

async function cartRequest(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({ ...body, sections: cartSectionIds(), sections_url: window.location.pathname })
  });
  const data = await res.json();
  if (!res.ok || data.status) throw new Error(data.description || data.message || text.error);
  return data;
}

async function addToCart(form) {
  const btns = $$('[data-atc], button[name="add"]', form.closest('[data-product-section]') || form);
  const errorBox = $('[data-form-error]', form);
  btns.forEach((b) => b.setAttribute('aria-busy', 'true'));
  if (errorBox) errorBox.hidden = true;
  const fd = new FormData(form);
  const items = [{ id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1) }];
  try {
    const data = await cartRequest(`${routes.cartAdd}.js`, { items });
    if (config.cartType === 'page') { window.location.href = routes.cart; return; }
    renderCartSections(data.sections);
    announce(text.added);
    openDialog(cartDrawer());
  } catch (err) {
    if (errorBox) { errorBox.textContent = err.message || text.error; errorBox.hidden = false; }
    else announce(err.message || text.error);
  } finally {
    btns.forEach((b) => b.removeAttribute('aria-busy'));
  }
}

document.addEventListener('submit', (e) => {
  const form = e.target.closest('form[data-product-form]');
  if (!form || !window.fetch) return;
  // Only intercept the "add to cart" submit, not the dynamic checkout button.
  if (e.submitter && e.submitter.name && e.submitter.name !== 'add') return;
  e.preventDefault();
  addToCart(form);
});

async function changeLine(lineEl, quantity) {
  lineEl.classList.add('is-loading');
  try {
    const data = await cartRequest(`${routes.cartChange}.js`, { line: Number(lineEl.dataset.line), quantity });
    renderCartSections(data.sections);
    announce(text.cartUpdated);
  } catch (err) {
    announce(err.message || text.error);
    lineEl.classList.remove('is-loading');
  }
}

document.addEventListener('click', (e) => {
  const qtyBtn = e.target.closest('[data-qty-change]');
  if (qtyBtn) {
    const wrap = qtyBtn.closest('.qty');
    const input = $('input', wrap);
    const step = Number(qtyBtn.dataset.qtyChange);
    const min = Number(input.min || 0);
    const max = input.max ? Number(input.max) : Infinity;
    const next = Math.min(max, Math.max(min, Number(input.value || 0) + step));
    input.value = next;
    const line = qtyBtn.closest('.cart-line');
    if (line) changeLine(line, next);
    return;
  }
  const remove = e.target.closest('[data-line-remove]');
  if (remove) { e.preventDefault(); changeLine(remove.closest('.cart-line'), 0); return; }
  const cartLink = e.target.closest('[data-cart-open]');
  if (cartLink && config.cartType !== 'page' && cartDrawer()) { e.preventDefault(); openDialog(cartDrawer(), cartLink); }
});
document.addEventListener('change', (e) => {
  const input = e.target.closest('[data-line-qty]');
  if (input) changeLine(input.closest('.cart-line'), Math.max(0, Number(input.value || 0)));
  const sort = e.target.closest('[data-autosubmit]');
  if (sort) sort.form.submit();
});

/* ---------- Variant picker ---------- */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('[data-product-section]');
    this.product = JSON.parse($('[data-product-json]', this.section)?.textContent || '{}');
    this.addEventListener('change', () => this.update());
  }
  update() {
    const selected = $$('fieldset', this).map((fs) => $('input:checked', fs)?.value);
    const variant = (this.product.variants || []).find((v) => v.options.every((o, i) => o === selected[i]));
    $$('[data-option-selected]', this).forEach((el, i) => { el.textContent = selected[i] || ''; });
    const s = this.section;
    const atcs = $$('[data-atc]', s);
    if (!variant) {
      atcs.forEach((b) => { b.disabled = true; $('[data-atc-text]', b).textContent = text.unavailable; });
      return;
    }
    $('[data-variant-input]', s).value = variant.id;
    const cur = $('[data-price-current]', s);
    const cmp = $('[data-price-compare]', s);
    if (cur) cur.textContent = variant.price;
    if (cmp) { cmp.hidden = !variant.compare; $('[data-compare-value]', cmp).textContent = variant.compare || ''; }
    $('[data-price]', s)?.classList.toggle('price--sale', !!variant.compare);
    const stock = $('[data-stock]', s);
    if (stock) {
      stock.classList.toggle('stock--in', variant.available);
      stock.classList.toggle('stock--out', !variant.available);
      $('[data-stock-text]', stock).textContent = variant.available ? stock.dataset.in || $('[data-stock-text]', stock).textContent : text.soldOut;
    }
    atcs.forEach((b) => { b.disabled = !variant.available; $('[data-atc-text]', b).textContent = variant.available ? text.addToCart : text.soldOut; });
    $('[data-dynamic-checkout]', s)?.toggleAttribute('hidden', !variant.available);
    const sp = $('[data-sticky-price]', s);
    if (sp) sp.firstChild.textContent = `${variant.price}${sp.querySelector('[data-sticky-variant]') ? ' · ' : ''}`;
    const sv = $('[data-sticky-variant]', s);
    if (sv) sv.textContent = variant.title;
    if (variant.media) s.dispatchEvent(new CustomEvent('gallery:goto', { detail: variant.media }));
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }
}
customElements.define('variant-picker', VariantPicker);

// Remember the "in stock" label so it can be restored after selecting a sold-out variant.
$$('[data-stock]').forEach((el) => { if (el.classList.contains('stock--in')) el.dataset.in = $('[data-stock-text]', el).textContent; });

/* ---------- Product gallery (scroll-snap) ---------- */
$$('[data-gallery]').forEach((gallery) => {
  const track = $('[data-gallery-track]', gallery);
  const slides = $$('.gallery__slide', track);
  const thumbs = $$('[data-thumb]', gallery);
  const counter = $('[data-gallery-index]', gallery);
  const goto = (id) => {
    const slide = slides.find((s) => s.dataset.mediaId === String(id));
    if (slide) track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'smooth' });
  };
  thumbs.forEach((t) => t.addEventListener('click', () => goto(t.dataset.thumb)));
  gallery.closest('[data-product-section]')?.addEventListener('gallery:goto', (e) => goto(e.detail));
  if (!('IntersectionObserver' in window) || slides.length < 2) return;
  const io = new IntersectionObserver((entries) => {
    entries.forEach((en) => {
      if (!en.isIntersecting) return;
      const i = slides.indexOf(en.target);
      if (counter) counter.textContent = i + 1;
      thumbs.forEach((t, j) => t.setAttribute('aria-current', j === i ? 'true' : 'false'));
      // Pause videos that leave the viewport.
      slides.forEach((s, j) => { if (j !== i) $('video', s)?.pause(); });
    });
  }, { root: track, threshold: 0.6 });
  slides.forEach((s) => io.observe(s));
});

/* ---------- Sticky add-to-cart (mobile) ---------- */
$$('[data-sticky-buy]').forEach((bar) => {
  const main = $('.pdp__atc', bar.closest('[data-product-section]'));
  if (!main || !('IntersectionObserver' in window)) return;
  bar.hidden = false;
  new IntersectionObserver(([en]) => {
    bar.classList.toggle('is-visible', !en.isIntersecting && en.boundingClientRect.top < 0);
  }).observe(main);
});

/* ---------- Lazy product recommendations ---------- */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.children.length) return;
    const load = async () => {
      try {
        const res = await fetch(this.dataset.url);
        const fresh = $('product-recommendations', parseHTML(await res.text()));
        if (fresh?.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
      } catch (_) { /* recommendations are optional */ }
    };
    if (!('IntersectionObserver' in window)) { load(); return; }
    const io = new IntersectionObserver(([en]) => { if (en.isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '600px 0px' });
    io.observe(this);
  }
}
customElements.define('product-recommendations', ProductRecommendations);

/* ---------- Collection description "read more" ---------- */
// The clamp class is set server-side (no layout shift); JS only toggles it.
$$('[data-readmore]').forEach((el) => {
  const btn = el.nextElementSibling?.matches('[data-readmore-toggle]') ? el.nextElementSibling : null;
  if (!btn) return;
  btn.addEventListener('click', () => {
    const open = el.classList.toggle('is-clamped') === false;
    btn.setAttribute('aria-expanded', String(open));
  });
});

/* ---------- Order tracking via WhatsApp ---------- */
$$('[data-tracking-form]').forEach((form) => {
  form.addEventListener('submit', () => {
    $('[data-tracking-text]', form).value = form.dataset.prefix + $('[data-tracking-input]', form).value.trim();
  });
});
